import React, { useCallback, useEffect, useRef, useState } from "react";
import BrandLogo from "./shared/BrandLogo";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { toast } from "@/lib/toast";

import OfflineBanner from "./OfflineBanner";
import { ErrorBoundary } from "./ErrorBoundary";
import GlobalSearchModal from "../features/search/components/GlobalSearchModal";
import SideDrawer from "./shared/SideDrawer";
import Settings from "../pages/Settings";
import { useConfirm } from "./ConfirmDialog";
import NotificationsPanel from "./NotificationsPanel";
import { NotificationsProvider, useNotificationsCtx } from "../contexts/NotificationsContext";
import { KeviaContextProvider } from "../contexts/KeviaContext";
import { useAnalytics } from "../hooks/useAnalytics";

import {
  Home,
  Church,
  HeartHandshake,
  HandHeart,
  MapPinned,
  NotebookPen,
  CalendarDays,
  Megaphone,
  Search,
  Bell,
  Settings as SettingsIcon,
  Users,
} from "lucide-react";

import { useDistrict } from "../lib/district-context";
import { useReadonly } from "../hooks/useReadonly";
import { KeviaFAB, KeviaFloatingPanel } from "./kevia/KeviaPanel";

const DISTRICT_TAB = { to: "/district",  icon: MapPinned, label: "Distrito", testId: "nav-district" };
const FAMILIES_TAB = { to: "/families",  icon: Users,     label: "Familias", testId: "nav-families" };

const BASE_TABS = [
  { to: "/",        icon: Home,          label: "Inicio",   testId: "nav-home"    },
  { to: "/churches",icon: Church,        label: "Iglesias", testId: "nav-churches"},
  { to: "/visits",  icon: HeartHandshake,label: "Visitas",  testId: "nav-visits"  },
  { to: "/prayers", icon: HandHeart,     label: "Oración",  testId: "nav-prayers" },
  null, // slot replaced at runtime
  { to: "/notes",   icon: NotebookPen,   label: "Notas",    testId: "nav-notes"   },
];

export default function Layout() {
  const loc = useLocation();
  const nav = useNavigate();

  const { loading, isCoordinator, userProfile } = useDistrict();
  const { bannerMessage, bannerVariant, waUpgradeUrl, isReadonly } = useReadonly();

  // Coordinator gets a dedicated 6-tab nav focused on their church.
  const tabs = isCoordinator ? [
    { to: "/coordinator-dashboard",                       icon: Home,          label: "Inicio",   testId: "nav-home"    },
    { to: userProfile?.church_id ? `/churches/${userProfile.church_id}` : "/coordinator-dashboard", icon: Church, label: "Iglesia", testId: "nav-church" },
    { to: "/visits",         icon: HeartHandshake, label: "Visitas",   testId: "nav-visits"         },
    { to: "/prayers",        icon: HandHeart,      label: "Oración",   testId: "nav-prayers"        },
    { to: "/agenda",         icon: CalendarDays,   label: "Agenda",    testId: "nav-agenda"         },
    { to: "/announcements",  icon: Megaphone,      label: "Anuncios",  testId: "nav-announcements"  },
  ] : BASE_TABS.map((t) => {
    if (t === null) return DISTRICT_TAB;
    return t;
  });

  const lastErrorRef       = useRef(0);
  const lastScreenshotRef  = useRef(null);
  const captureTimerRef    = useRef(null);

  const [keyboardVisible,     setKeyboardVisible]     = useState(false);
  const [searchOpen,          setSearchOpen]          = useState(false);
  const [settingsOpen,        setSettingsOpen]        = useState(false);
  const [notifOpen,           setNotifOpen]           = useState(false);
  const [keviaOpen,           setKeviaOpen]           = useState(false);
  const [backgroundRoute,     setBackgroundRoute]     = useState(null);
  const [backgroundScreenshot,setBackgroundScreenshot]= useState(null);

  // Confirm dialog owned at Layout level so it portals outside SideDrawer's React tree,
  // preventing React event-bubbling from closing the drawer when the dialog resolves.
  const [settingsConfirm, SettingsConfirmUI] = useConfirm();

  // Capture a low-resolution screenshot of the current page 800ms after each navigation.
  // Stored in a ref so it's always available instantly when the settings drawer opens.
  // Dynamic import keeps html-to-image out of the initial bundle.
  useEffect(() => {
    clearTimeout(captureTimerRef.current);
    captureTimerRef.current = setTimeout(() => {
      import('html-to-image')
        .then(({ toJpeg }) => {
          const el = document.getElementById('keves-capture');
          if (!el) return null;
          return toJpeg(el, { quality: 0.6, pixelRatio: 0.5, skipFonts: true });
        })
        .then((dataUrl) => { if (dataUrl) lastScreenshotRef.current = dataUrl; })
        .catch(() => {});
    }, 800);
    return () => clearTimeout(captureTimerRef.current);
  }, [loc.pathname]);

  // Close floating panel when navigating to the full Kevia page.
  useEffect(() => {
    if (loc.pathname === "/kevia") setKeviaOpen(false);
  }, [loc.pathname]);

  // Snapshot the background context (route + screenshot) before the drawer opens.
  const handleOpenSettings = useCallback(() => {
    setBackgroundRoute(loc.pathname);
    setBackgroundScreenshot(lastScreenshotRef.current);
    setSettingsOpen(true);
  }, [loc.pathname]);

  const {
    unreadCount,
    notifications,
    loading: notifLoading,
    markRead: markNotifRead,
    dismissAll,
    clear,
    reload: reloadNotifs,
    refresh: refreshNotifs,
    pendingAccessRequestsCount,
    pendingAccessRequests,
  } = useNotificationsCtx();

  // Combined badge: pastoral notifications + pending access requests (admin only)
  const totalBadgeCount = unreadCount + pendingAccessRequestsCount;

  useAnalytics();

  // Mark notification as read, close the panel, then navigate.
  const handleNotifNavigate = useCallback((id, route) => {
    markNotifRead(id);
    setNotifOpen(false);
    nav(route);
  }, [nav, markNotifRead]);

  const isDashboard = loc.pathname === "/" || loc.pathname === "/coordinator-dashboard";

  const isActive = (to) => {
    if (to === "/" || to === "/coordinator-dashboard") {
      return loc.pathname === to;
    }
    return loc.pathname.startsWith(to);
  };

  // Single handler for all programmatic panel-open events from child pages
  useEffect(() => {
    const onSearch   = () => setSearchOpen(true);
    const onSettings = () => handleOpenSettings();
    const onNotifs   = () => { refreshNotifs(); setNotifOpen(true); };

    window.addEventListener("keves:openSearch",        onSearch);
    window.addEventListener("keves:openSettings",      onSettings);
    window.addEventListener("keves:openNotifications", onNotifs);

    return () => {
      window.removeEventListener("keves:openSearch",        onSearch);
      window.removeEventListener("keves:openSettings",      onSettings);
      window.removeEventListener("keves:openNotifications", onNotifs);
    };
  }, [refreshNotifs, handleOpenSettings]);

  // Hide bottom nav when software keyboard is visible (iOS/Android)
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const handler = () => {
      setKeyboardVisible(vv.height < window.innerHeight * 0.75);
    };
    vv.addEventListener('resize', handler);
    return () => vv.removeEventListener('resize', handler);
  }, []);

  // ⌘K / Ctrl+K shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // 🔥 MANEJO GLOBAL DE ERRORES ASÍNCRONOS
  // Captura promesas rechazadas no manejadas
  // sin romper la navegación ni la UI.
  useEffect(() => {
    const THROTTLE_MS = 4000;

    const shouldThrottle = () => {
      const now = Date.now();

      if (now - lastErrorRef.current < THROTTLE_MS) {
        return true;
      }

      lastErrorRef.current = now;
      return false;
    };

    const handleUnhandledRejection = (event) => {
      console.error(
        "[Global Async Error]:",
        event.reason
      );

      // 🔕 Ignorar abortos intencionales
      if (event.reason?.name === "AbortError") {
        return;
      }

      // 🔕 Ignorar errores marcados manualmente
      if (event.reason?.__skipOnline) {
        return;
      }

      // 🔕 Ignorar fallos normales cuando no hay internet
      if (!navigator.onLine) {
        return;
      }

      // 🔕 Evitar spam de toasts
      if (shouldThrottle()) {
        return;
      }

      toast.error(
        "Ocurrió un problema procesando tu solicitud",
        {
          description:
            "Por favor, inténtalo nuevamente.",
        }
      );
    };

    // ⚠️ window.error puede capturar:
    // - extensiones
    // - scripts externos
    // - assets
    // - errores ajenos a la app
    //
    // Por eso SOLO registramos en consola
    // y NO mostramos toast global.
    const handleWindowError = (event) => {
      console.error(
        "[Global Runtime Error]:",
        event.error || event.message
      );

      // Algunos errores de assets
      // llegan sin event.error
      if (!event.error) {
        return;
      }
    };

    window.addEventListener(
      "unhandledrejection",
      handleUnhandledRejection
    );

    window.addEventListener(
      "error",
      handleWindowError
    );

    return () => {
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );

      window.removeEventListener(
        "error",
        handleWindowError
      );
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[var(--kv-page-bg)]">
        <div className="text-center space-y-3">

          <div className="animate-pulse h-9 w-9 rounded-full bg-[#1b4f9c]/20 mx-auto" />

          <p className="text-sm text-slate-500 dark:text-slate-400">
            Cargando...
          </p>

        </div>
      </div>
    );
  }

  return (
    <KeviaContextProvider>
    <NotificationsProvider>
    <div className="min-h-[100dvh] bg-[var(--kv-page-bg)]">

      {/* 🔥 APP WRAPPER */}
      <div
        className="
          relative
          w-full
          min-h-[100dvh]
          [overflow-x:clip]
        "
        data-testid="app-container"
      >

        {/* 🔥 TOPBAR */}
        {!isDashboard && (
          <header
            className="
              border-b border-slate-100 dark:border-slate-800
              bg-white dark:bg-slate-900
            "
          >
            <div className="mx-auto w-full max-w-screen-sm md:max-w-3xl">
              <div className="flex items-center gap-3 px-4 md:px-6 pt-safe-header pb-3">

                {/* LOGO — idéntico a DashboardHeader */}
                <div className="h-11 w-11 rounded-icon bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm flex items-center justify-center shrink-0">
                  <BrandLogo variant="auto" className="h-7 object-contain" />
                </div>

                {/* BRANDING — copia exacta de DashboardHeader */}
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 dark:text-slate-500">
                    {isCoordinator ? "Coordinador local" : "Plataforma pastoral"}
                  </p>
                  <h1 className="font-bold text-slate-900 dark:text-slate-100 leading-tight">
                    Kéves App
                  </h1>
                </div>

                {/* GLOBAL ACTIONS — mismo gap y border que DashboardHeader */}
                <div className="flex items-center gap-2 ml-auto shrink-0">

                  {/* SEARCH */}
                  <button
                    onClick={() => setSearchOpen(true)}
                    aria-label="Buscar"
                    className="h-11 w-11 rounded-full bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/60 shadow-sm flex items-center justify-center text-slate-500 dark:text-slate-400 transition-all duration-200 ease-out hover:shadow-md hover:text-slate-700 dark:hover:text-slate-200 active:scale-[0.97]"
                  >
                    <Search size={16} strokeWidth={2} />
                  </button>

                  {/* NOTIFICATIONS */}
                  <button
                    onClick={() => { refreshNotifs(); setNotifOpen(true); }}
                    aria-label="Notificaciones"
                    className="relative h-11 w-11 rounded-full bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/60 shadow-sm flex items-center justify-center text-slate-500 dark:text-slate-400 transition-all duration-200 ease-out hover:shadow-md hover:text-slate-700 dark:hover:text-slate-200 active:scale-[0.97]"
                  >
                    <Bell size={16} strokeWidth={2} />
                    {totalBadgeCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 h-[14px] min-w-[14px] px-0.5 rounded-full bg-red-500 border border-white dark:border-slate-800 flex items-center justify-center">
                        <span className="text-[8px] font-bold text-white leading-none">
                          {totalBadgeCount > 9 ? "9+" : totalBadgeCount}
                        </span>
                      </span>
                    )}
                  </button>

                  {/* SETTINGS */}
                  <button
                    onClick={handleOpenSettings}
                    aria-label="Configuración"
                    className="h-11 w-11 rounded-full bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/60 shadow-sm flex items-center justify-center text-slate-500 dark:text-slate-400 transition-all duration-200 ease-out hover:shadow-md hover:text-slate-700 dark:hover:text-slate-200 active:scale-[0.97]"
                  >
                    <SettingsIcon size={16} strokeWidth={2} />
                  </button>

                </div>

              </div>
            </div>
          </header>
        )}

        {/* 🔥 OFFLINE */}
        <OfflineBanner />

        {/* 🔥 SUBSCRIPTION BANNER */}
        {bannerMessage && (
          <div className={`sticky top-0 z-40 text-white text-[12px] font-semibold py-2 px-4 leading-tight flex items-center justify-center gap-3 ${
            bannerVariant === "readonly"
              ? "bg-red-600 dark:bg-red-700"
              : "bg-amber-500 dark:bg-amber-600"
          }`}>
            <span className="flex-1 text-center">{bannerMessage}</span>
            {isReadonly && (
              <a
                href={waUpgradeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 bg-white/20 hover:bg-white/30 active:bg-white/40 rounded-full px-3 py-0.5 text-[11px] font-bold transition-all duration-150 whitespace-nowrap"
              >
                Contactar →
              </a>
            )}
          </div>
        )}

        {/* 🔥 CONTENT — centered column on tablet/iPad */}
        <div id="keves-capture" className="mx-auto w-full max-w-screen-sm md:max-w-3xl min-h-[100dvh]">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </div>

        {/* STATUS BAR FADE — softens content scrolling under iOS status bar */}
        <div
          aria-hidden="true"
          className="fixed top-0 inset-x-0 z-[45] pointer-events-none bg-gradient-to-b from-[#f8fafc] dark:from-[#050d1e] to-transparent"
          style={{ height: 'calc(env(safe-area-inset-top) + 20px)' }}
        />

        {/* 🔥 FLOATING NAVBAR
             Hidden when software keyboard is open (visualViewport shrinks).
             [will-change:transform] forces a dedicated GPU compositor layer on iOS Safari.
             Without it, Safari repaints the nav in the same pass as scrolling content,
             causing the "floating" visual artifact on fast scroll. */}
        <div
          className="
            fixed
            bottom-0
            inset-x-0
            z-50
            [will-change:transform]
            transition-transform
            duration-200
          "
          style={{ transform: keyboardVisible ? 'translateY(100%)' : 'translateY(0)' }}
        >

          <nav
            className="
            w-full
            max-w-screen-sm md:max-w-3xl
            mx-auto
            border-t
            border-slate-200/60 dark:border-slate-800/60
            bg-white/95 dark:bg-slate-900/95
            backdrop-blur-md
            pb-[env(safe-area-inset-bottom)]
            "
            data-testid="bottom-nav"
          >

            <div
              className="
                grid
                grid-cols-6
                items-center
                px-2
              py-1.5
              "
            >

              {tabs.map((t) => {
                const Icon = t.icon;

                const active = isActive(t.to);

                return (
                  <button
                    key={t.to}
                    onClick={() => nav(t.to)}
                    data-testid={t.testId}
                    aria-label={t.label}
                    aria-current={active ? "page" : undefined}
                    className={`
                      relative
                      flex
                      flex-col
                      items-center
                      justify-center
                      gap-1
                      rounded-[14px]
                      px-1
                      py-2.5
                      transition-all
                      duration-200
                      ${
                        active
                          ? "text-brand dark:text-blue-400"
                          : "text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                      }
                    `}
                  >

                    {/* 🔥 ACTIVE BG */}
                    {active && (
                      <div
                        className="
                          absolute
                          inset-0
                        rounded-[14px]
                        bg-brand-light/80
                        "
                      />
                    )}

                    {/* 🔥 ICON */}
                    <div className="relative z-10">

                      <Icon
                        size={21}
                        strokeWidth={active ? 2.6 : 2}
                        className={
                          active
                            ? "scale-105"
                            : ""
                        }
                      />

                    </div>

                    {/* 🔥 LABEL */}
                    <span
                      className={`
                        relative
                        z-10
                        text-[10px]
                        w-full
                        text-center
                        truncate
                        ${
                          active
                            ? "font-semibold"
                            : "font-medium"
                        }
                      `}
                    >
                      {t.label}
                    </span>

                  </button>
                );
              })}

            </div>

          </nav>

        </div>

      </div>

      {/* 🔥 GLOBAL SEARCH MODAL */}
      <GlobalSearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
      />

      {/* 🔥 SETTINGS DRAWER */}
      <SideDrawer
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        title="Ajustes"
      >
        <Settings
          inDrawer
          onClose={() => setSettingsOpen(false)}
          confirm={settingsConfirm}
          backgroundRoute={backgroundRoute}
          backgroundScreenshot={backgroundScreenshot}
        />
      </SideDrawer>

      {/* 🔥 NOTIFICATIONS DRAWER */}
      <SideDrawer
        isOpen={notifOpen}
        onClose={() => setNotifOpen(false)}
        title="Notificaciones"
      >
        <NotificationsPanel
          notifications={notifications}
          accessRequests={pendingAccessRequests}
          loading={notifLoading}
          onDismissAll={dismissAll}
          onClear={clear}
          onNavigate={handleNotifNavigate}
        />
      </SideDrawer>

      {/* 🔥 KEVIA FLOATING ASSISTANT
           FAB oculto en /kevia para evitar duplicación.
           Panel montado sobre todos los drawers excepto alertas. */}
      <KeviaFAB
        isOpen={keviaOpen}
        onClick={() => setKeviaOpen((p) => !p)}
        hidden={loc.pathname === "/kevia"}
      />
      <KeviaFloatingPanel
        isOpen={keviaOpen}
        onClose={() => setKeviaOpen(false)}
      />

    </div>

    {/* ConfirmDialog for Settings — rendered outside all SideDrawers to prevent
        React event-bubbling through the drawer tree from interfering with dialog actions. */}
    {SettingsConfirmUI}

    </NotificationsProvider>
    </KeviaContextProvider>
  );
}